// Nombre de horario, ordenado para mostrar.
//
// El nombre del horario se captura a mano y en la base hay 33 maneras distintas
// de separar las dos horas: "-", "am -", "a.m. -", "am a", "p.m. -"... Dos mil
// estan en am/pm y mil cien en formato de 24. Esto no arregla la base: lee lo
// que haya y devuelve siempre la misma forma.
//
//     "Sabado 11:00 a.m - 3:00 p.m"  ->  "Sábado · 11:00 am – 3:00 pm"
//     "Miercoles  12:00 - 15:30"     ->  "Miércoles · 12:00 pm – 3:30 pm"
//
// Corrido contra los 3 439 horarios de produccion: 3 287 se ordenan (95.6 %),
// 149 no traen ninguna hora escrita ("Agosto 2025") y 3 son irresolubles.
// Lo que no se puede leer con certeza se devuelve tal cual se capturo: nunca
// se inventa un horario.
//
// Cuando se arregle el dato de horas de sesion, esto se cambia por ese dato y
// el archivo se puede borrar.

const DIAS = ["lunes", "martes", "miercoles", "jueves", "viernes", "sabado", "domingo"]

const BONITO : {[clave : string] : string} =
{
	lunes   : "Lunes",   martes  : "Martes", miercoles : "Miércoles", jueves : "Jueves",
	viernes : "Viernes", sabado  : "Sábado", domingo   : "Domingo"
}

// Regla de Esteban: ningun horario empieza antes de las 7 de la mañana ni
// termina despues de las 10 de la noche. Sirve para desempatar: si el am/pm
// escrito cae fuera de esa ventana, se toma como error de captura.
const ABRE   =  7 * 60
const CIERRA = 22 * 60
const MAXIMO = 10 * 60

const RANGO = new RegExp(
	"(?:(\\d{1,2})\\s*[:.]\\s*(\\d{2})|(\\d{1,2}))" +
	"\\s*(a\\s*\\.?\\s*m\\s*\\.?|p\\s*\\.?\\s*m\\s*\\.?)?" +
	"\\s*(?:-|–|—|a|hasta)\\s*" +
	"(?:(\\d{1,2})\\s*[:.]\\s*(\\d{2})|(\\d{1,2}))" +
	"\\s*(a\\s*\\.?\\s*m\\s*\\.?|p\\s*\\.?\\s*m\\s*\\.?)?",
	"i"
)

// Algunos vienen sin separador: "Domingo 10:00 13:00". Se acepta el espacio solo
// cuando las dos horas traen minutos; si no, un numero suelto del titulo
// ("Seminario 3 Sabado 8:00 - 11:00") se leeria como la hora de inicio.
const RANGO_SIN_SEPARADOR = new RegExp(
	"(\\d{1,2})\\s*[:.]\\s*(\\d{2})" +
	"\\s*(a\\s*\\.?\\s*m\\s*\\.?|p\\s*\\.?\\s*m\\s*\\.?)?" +
	"\\s+" +
	"(\\d{1,2})\\s*[:.]\\s*(\\d{2})" +
	"\\s*(a\\s*\\.?\\s*m\\s*\\.?|p\\s*\\.?\\s*m\\s*\\.?)?",
	"i"
)

const sinAcentos = (texto : string) : string =>
	texto.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase()

/** Cada lectura posible de una hora, con si coincide con el am/pm escrito. */
const lecturas = (hora : number, minutos : number, meridiano : string | undefined)
	: {valor : number, escrito : boolean}[] =>
{
	if(hora >= 13) return [{valor : hora * 60 + minutos, escrito : true}]

	let escrito : number | null = null

	if(meridiano)
	{
		const limpio = meridiano.replace(/[\s.]/g, "").toLowerCase()
		let h = hora
		if(limpio === "pm" && hora !== 12) h = hora + 12
		if(limpio === "am" && hora === 12) h = 0
		escrito = h * 60 + minutos
	}

	const am = (hora % 12) * 60 + minutos
	const pm = ((hora % 12) + 12) * 60 + minutos
	const unicos = am === pm ? [am] : [am, pm]

	return unicos.map(valor => ({valor : valor, escrito : valor === escrito}))
}

const resuelve = (
	h1 : number, m1 : number, mer1 : string | undefined,
	h2 : number, m2 : number, mer2 : string | undefined
) : [number, number] | null =>
{
	const todas : [number, number, number][] = []

	for(const a of lecturas(h1, m1, mer1))
	{
		for(const b of lecturas(h2, m2, mer2))
		{
			if(b.valor > a.valor)
			{
				todas.push([(a.escrito ? 1 : 0) + (b.escrito ? 1 : 0), a.valor, b.valor])
			}
		}
	}

	if(todas.length === 0) return null

	// Escrito en formato de 24 horas no hay nada que elegir: se respeta tal cual,
	// aunque sea una clase de 20:30 a 22:30.
	if(todas.length === 1) return [todas[0][1], todas[0][2]]

	const buenas = todas
		.filter(t => (t[2] - t[1]) <= MAXIMO && t[1] >= ABRE && t[2] <= CIERRA)
		.sort((x, y) => y[0] - x[0])

	if(buenas.length === 0) return null
	// Si dos posibilidades respetan lo escrito por igual, no se adivina.
	if(buenas.length > 1 && buenas[0][0] === buenas[1][0]) return null

	return [buenas[0][1], buenas[0][2]]
}

const enDoce = (total : number) : string =>
{
	const hora    = Math.floor(total / 60)
	const minutos = total % 60
	const sufijo  = hora < 12 ? "am" : "pm"
	const docena  = (hora % 12) === 0 ? 12 : (hora % 12)

	/*
		Espacio DURO entre la hora y el am/pm: con un espacio normal, en una columna
		estrecha se partia "1:00" en un renglon y "pm" en el siguiente, que se ve
		fatal y no se lee. (Esteban, 2026-09-21)
	*/
	return docena + ":" + String(minutos).padStart(2, "0") + "\u00A0" + sufijo
}

/*
	En las listas los días van en plural y sin el punto entre el día y la hora:
	"Sábados 8:00 am – 11:30 am" se lee como habla la gente (Esteban, 2026-09-23).
	Solo cambian Sábado y Domingo; los demás ya terminan en -s.
*/
export interface OpcionesHorario
{
	plural?    : boolean
	separador? : string
	/* "4:00 – 7:30 pm" en vez de "4:00 pm – 7:30 pm": si las dos horas son del mismo
	   meridiano, el am/pm se dice una sola vez (Esteban, 2026-09-23). */
	compacto?  : boolean
}

const enPlural = (etiqueta : string) : string =>
	etiqueta.replace(/\bSábado\b/g, "Sábados").replace(/\bDomingo\b/g, "Domingos")

const diasDe = (texto : string) : {etiqueta : string, resto : string} =>
{
	const plano    = sinAcentos(texto)
	const hallados = DIAS
		.filter(dia => new RegExp("\\b" + dia + "\\b").test(plano))
		.sort((a, b) => plano.indexOf(a) - plano.indexOf(b))

	if(hallados.length === 0) return {etiqueta : "", resto : texto}

	// "Lunes - Viernes" y "Lunes a Viernes" son un rango; lo demas es una lista.
	const esRango = hallados.length === 2 &&
		new RegExp("\\b" + hallados[0] + "\\b\\s*(?:-|–|a)\\s*\\b" + hallados[1] + "\\b").test(plano)

	let etiqueta : string
	if(esRango)                    etiqueta = BONITO[hallados[0]] + " a " + BONITO[hallados[1]]
	else if(hallados.length === 1) etiqueta = BONITO[hallados[0]]
	else                           etiqueta = hallados.slice(0, -1).map(d => BONITO[d]).join(", ") +
		" y " + BONITO[hallados[hallados.length - 1]]

	// Se quitan del texto por posicion, que es lo unico fiable con los acentos.
	let resto = ""
	let desde = 0
	const plantilla = plano

	for(const dia of hallados)
	{
		const donde = plantilla.indexOf(dia, desde)
		if(donde < 0) continue
		resto += texto.slice(desde, donde)
		desde  = donde + dia.length
	}
	resto += texto.slice(desde)

	return {etiqueta : etiqueta, resto : resto}
}

/**
 * "Sección B Domingo 11:00 am a 3:00pm" -> "Domingo · 11:00 am – 3:00 pm · Sección B"
 * Si no se puede leer con certeza, devuelve el nombre tal cual.
 */
export const horarioBonito = (nombre : any, opciones? : OpcionesHorario) : string =>
{
	const plural    = opciones?.plural === true
	const separador = opciones?.separador ?? " · "
	const original = String(nombre || "").replace(/\s+/g, " ").trim()
	if(!original) return ""

	let encontrado = RANGO.exec(original)
	let h1 : number, mm1 : number, mer1 : string | undefined
	let h2 : number, mm2 : number, mer2 : string | undefined

	if(encontrado !== null)
	{
		h1 = parseInt(encontrado[1] || encontrado[3], 10); mm1 = parseInt(encontrado[2] || "0", 10); mer1 = encontrado[4]
		h2 = parseInt(encontrado[5] || encontrado[7], 10); mm2 = parseInt(encontrado[6] || "0", 10); mer2 = encontrado[8]
	}
	else
	{
		encontrado = RANGO_SIN_SEPARADOR.exec(original)
		if(encontrado === null) return original
		h1 = parseInt(encontrado[1], 10); mm1 = parseInt(encontrado[2], 10); mer1 = encontrado[3]
		h2 = parseInt(encontrado[4], 10); mm2 = parseInt(encontrado[5], 10); mer2 = encontrado[6]
	}

	if(h1 > 24 || h2 > 24 || mm1 > 59 || mm2 > 59) return original

	const par = resuelve(h1, mm1, mer1, h2, mm2, mer2)
	if(par === null) return original

	const sinHoras = original.slice(0, encontrado.index) + " " + original.slice(encontrado.index + encontrado[0].length)
	const {etiqueta, resto} = diasDe(sinHoras)

	// Conectores que quedan colgando al quitar los nombres de los dias.
	/*
		"Sección A" termina en una "a" que no es conector: antes se borraba y quedaba "seccion" a secas
		(Acrilismo de Zulmy Deras en el calendario, 2026-11-02). La letra o el número de la sección se queda.
	*/
	const limpio = resto
		.replace(/[\s,;:./·-]+/g, " ")
		.trim()
		.replace(/^(?:a|y|al|hasta|de)(?:\s+|$)/i, "")
	// Con espacio delante, no \b: junto a una letra con acento \b corta la palabra ("Mixología" perdía la "a").
	const sobra = (/\bsecci[oó]n\s+\S{1,3}$/i.test(limpio) ? limpio : limpio.replace(/(?:^|\s+)(?:a|y|al|hasta|de)$/i, ""))
		.replace(/\bsecci[oó]n\s+(\S{1,3})\b/i, (_m : string, s : string) => "Sección " + s.toUpperCase())
		.replace(/\bsecci[oó]n\b/i, "Sección")
		.trim()
		// Pedazos sueltos del meridiano: "m", "mm" (mediodia), "M", "p m".
		.replace(/^[apm\s.,]{1,4}$/i, "")
		.trim()

	const partes : string[] = []
	if(etiqueta) partes.push(plural ? enPlural(etiqueta) : etiqueta)
	const mismoMeridiano = (par[0] < 12 * 60) === (par[1] < 12 * 60)
	partes.push(opciones?.compacto && mismoMeridiano
		? enDoce(par[0]).replace(/\u00A0(am|pm)$/, "") + " – " + enDoce(par[1])
		: enDoce(par[0]) + " – " + enDoce(par[1]))
	if(sobra) partes.push(sobra)

	//El separador solo aplica entre el día y la hora; lo que sobra del nombre va aparte.
	if(separador !== " · " && partes.length > 1)
	{
		const [primero, ...resto] = partes
		return primero + separador + resto.join(" · ")
	}

	return partes.join(" · ")
}

export default horarioBonito
